import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Tpl3plService, InventoryItem } from './tpl3pl.service';
import { GlobalSearchService } from './globalsearchservice.service';
import { RawResponseModalComponent } from '../system/tpl3pl/shared/raw-response-modal/raw-response-modal.component';

export type InventorySortColumn =
	| 'stockid'
	| 'warehouse_code'
	| 'quantity_on_hand'
	| 'quantity_available'
	| 'sync_status'
	| 'last_sync'
	| 'internal_qoh'
	| 'on_demand';

export interface Tpl3plInventoryListQuery {
	warehouse?: string;
	statusFilter?: string;
	search?: string;
	sort: InventorySortColumn;
	order: 'asc' | 'desc';
	pageIndex: number;
	pageSize: number;
	cacheBust?: boolean;
}

@Injectable({ providedIn: 'root' })
export class Tpl3plInventoryUiService {
	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {}

	warehouseParam(selectedWarehouse: string | undefined | null): string | undefined {
		const w = (selectedWarehouse ?? '').trim();
		return w === '' ? undefined : w;
	}

	getInventoryStatus$(warehouse?: string): Observable<any> {
		return this.tpl3plService.getInventoryStatus(warehouse, true);
	}

	getInventoryPage$(q: Tpl3plInventoryListQuery): Observable<{ data: InventoryItem[]; total: number }> {
		return this.tpl3plService
			.getInventory({
				warehouse: q.warehouse,
				status: q.statusFilter || undefined,
				search: (q.search || '').trim() || undefined,
				sort: q.sort,
				order: q.order,
				limit: q.pageSize,
				offset: q.pageIndex * q.pageSize,
				cacheBust: q.cacheBust,
			})
			.pipe(map((res) => ({ data: res.data || [], total: res.total ?? 0 })));
	}

	refreshStatusAndList$(q: Tpl3plInventoryListQuery): Observable<{ status: any; data: InventoryItem[]; total: number }> {
		const w = q.warehouse;
		return forkJoin({
			status: this.tpl3plService.getInventoryStatus(w, true),
			list: this.tpl3plService.getInventory({
				warehouse: w,
				status: q.statusFilter || undefined,
				search: (q.search || '').trim() || undefined,
				sort: q.sort,
				order: q.order,
				limit: q.pageSize,
				offset: q.pageIndex * q.pageSize,
				cacheBust: q.cacheBust,
			}),
		}).pipe(
			map(({ status, list }) => ({
				status,
				data: list.data || [],
				total: list.total ?? 0,
			}))
		);
	}

	openInventorySyncRawDialog(
		warehouse: string | undefined,
		onAfterSent: () => void,
		setLoading?: (v: boolean) => void
	): void {
		setLoading?.(true);
		this.tpl3plService.getInventorySyncPreview(warehouse).subscribe({
			next: (res) => {
				setLoading?.(false);
				if (!res.success) {
					this.globalSearchService.showNotification(res.message || 'Could not load request preview', 'warning', 'bottom', 'right');
					return;
				}
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'inventory',
						warehouse,
						content: { request_payload: res.request_payload },
						onSent: () => onAfterSent(),
					},
					panelClass: 'raw-response-dialog',
				});
			},
			error: () => {
				setLoading?.(false);
				this.globalSearchService.showNotification('Could not load request preview', 'danger', 'bottom', 'right');
			},
		});
	}
}
